/**
 * TOSS Web Application — Client Portal query keys
 *
 * TanStack Query key factory for `/api/client/*`, namespaced under a `'client'` root and kept
 * strictly separate from `queryKeys` in `src/app/queryClient.ts`.
 *
 * That separation is not cosmetic. `queryKeys.applications.detail(id)` caches an
 * `ApplicationFullData` — an internal DTO with mechanics, prices and payment status — while
 * `clientKeys.applications.detail(id)` caches a `ClientApplicationFullDto` for the SAME id.
 * Sharing a key would let one audience's mutation invalidate (or, worse, hand back) the other
 * audience's payload. Never reuse the internal factory here, and never widen this one to
 * internal screens.
 *
 * Every key starts with `'client'`, so signing out can drop the whole portal cache in one call:
 * `queryClient.removeQueries({ queryKey: clientKeys.all })`.
 *
 * Lives outside `features/client/**` for the same reason the API layer does: the ESLint boundary
 * (decision Р6) makes a static import of `features/client/**` an error everywhere else, so
 * anything shared with layout chrome has to live in `hooks/` or `api/`.
 */

import type {
  ClientApplicationFilterRequest,
  ClientApprovalFilterRequest,
  ClientContainerFilterRequest,
  ClientDocumentFilterRequest,
  ClientNotificationFilterRequest,
  ClientPagedRequest,
} from '@/api/clientPortal/clientPortal.types';
import type { DuplicateCheckKeys } from '@/lib/duplicateHint';

/** Root segment of every client-portal query key. */
const CLIENT_ROOT = 'client';

/**
 * Query key factory for the customer portal.
 *
 * @example
 * ```ts
 * useQuery({
 *   queryKey: clientKeys.applications.list(filter),
 *   queryFn: () => getClientApplications(filter),
 * });
 *
 * // after creating an application
 * queryClient.invalidateQueries({ queryKey: clientKeys.applications.lists() });
 * ```
 */
export const clientKeys = {
  /** Prefix of every portal key — use it to drop the entire portal cache at logout. */
  all: [CLIENT_ROOT] as const,

  applications: {
    all: [CLIENT_ROOT, 'applications'] as const,
    /** Every list page, regardless of filter — the invalidation target after create/cancel. */
    lists: () => [...clientKeys.applications.all, 'list'] as const,
    list: (filter?: ClientApplicationFilterRequest) =>
      [...clientKeys.applications.lists(), filter ?? {}] as const,
    details: () => [...clientKeys.applications.all, 'detail'] as const,
    detail: (id: string) => [...clientKeys.applications.details(), id] as const,
    /**
     * Audit history of one application. `params` is optional so that
     * `clientKeys.applications.history(id)` stays a valid invalidation prefix for every page of
     * it.
     */
    history: (id: string, params?: ClientPagedRequest) =>
      params === undefined
        ? ([...clientKeys.applications.detail(id), 'history'] as const)
        : ([...clientKeys.applications.detail(id), 'history', params] as const),
    /**
     * The duplicate hint of the create / edit form — keyed by the settled key set, the ACTIVE
     * COMPANY included (`scopeId`): the body carries no company, so the key is what discards an
     * answer that arrives after a company switch.
     */
    duplicateCheck: (keys: DuplicateCheckKeys) =>
      [...clientKeys.applications.all, 'duplicate-check', keys] as const,
  },

  /** Movement directions lookup (review decision Р4) — a dropdown source, cached app-wide. */
  directions: () => [CLIENT_ROOT, 'directions'] as const,

  /**
   * Application templates (`/api/client/application-templates`, release Р5).
   *
   * `list()` takes no filter — the endpoint answers a flat, unpaginated, unfiltered array
   * (contract §1), so there is no per-filter cache entry to key the way
   * {@link applications}.list does. `detail(id)` caches the FULL DTO the edit modal and the
   * create form's «Заполнить из шаблона» both hydrate from before touching anything (blocker
   * B1) — a shape the list row never carries.
   */
  templates: {
    all: [CLIENT_ROOT, 'templates'] as const,
    list: () => [...clientKeys.templates.all, 'list'] as const,
    details: () => [...clientKeys.templates.all, 'detail'] as const,
    detail: (id: string) => [...clientKeys.templates.details(), id] as const,
  },

  /**
   * The messenger, keyed by chat.
   *
   * `detail(id)` is a PREFIX and holds nothing itself: the chat's own card lives one segment
   * deeper, under `card(id)`. That looks like ceremony and is not. `invalidateQueries` matches by
   * prefix, and the realtime layer has a «refresh the metadata, leave the history alone» path —
   * it fires on every read receipt and on every unread tick, and the whole point of it is that the
   * message pages do NOT refetch under the reader's thumb. Put the card directly on `detail(id)`
   * and that path starts reloading every loaded page of the conversation.
   *
   * So: `detail(id)` invalidates everything about one chat, `card(id)` invalidates only its
   * header.
   */
  chat: {
    all: [CLIENT_ROOT, 'chat'] as const,

    /** Every chat of the active company. The badge reads the unfiltered entry. */
    lists: () => [...clientKeys.chat.all, 'list'] as const,
    list: (search?: string) => [...clientKeys.chat.lists(), search ?? ''] as const,

    /** Prefix of everything about one chat — never queried directly. */
    details: () => [...clientKeys.chat.all, 'detail'] as const,
    detail: (chatId: string) => [...clientKeys.chat.details(), chatId] as const,

    /** The chat's own card: title, unread count, read state. */
    card: (chatId: string) => [...clientKeys.chat.detail(chatId), 'card'] as const,
    /** Cursor-paged message history; the cursor belongs to the infinite query, not the key. */
    messages: (chatId: string) => [...clientKeys.chat.detail(chatId), 'messages'] as const,
    /** Hits of one search term inside the conversation. */
    search: (chatId: string, query: string) =>
      [...clientKeys.chat.detail(chatId), 'search', query] as const,
    /** The conversation's attachments, cursor-paged like the history. */
    files: (chatId: string) => [...clientKeys.chat.detail(chatId), 'files'] as const,
    /** Who takes part in the conversation, both sides. */
    members: (chatId: string) => [...clientKeys.chat.detail(chatId), 'members'] as const,
  },

  notifications: {
    all: [CLIENT_ROOT, 'notifications'] as const,
    lists: () => [...clientKeys.notifications.all, 'list'] as const,
    list: (filter?: ClientNotificationFilterRequest) =>
      [...clientKeys.notifications.lists(), filter ?? {}] as const,
    unreadCount: () => [...clientKeys.notifications.all, 'unread-count'] as const,
  },

  /**
   * The portal home screen (`GET /api/client/dashboard`) — release Р2.
   *
   * One key for one request: the endpoint answers with counters, recent applications,
   * recent documents and recent notifications together, so the screen has a single
   * loading state and a single retry instead of four that can disagree with each other.
   */
  dashboard: () => [CLIENT_ROOT, 'dashboard'] as const,

  /**
   * Свёрнутая «Аналитика» главной (`GET /api/client/dashboard/activity`) — 31.08.2026.
   *
   * Свой ключ, потому что и запрос свой: блок закрыт по умолчанию и грузится только когда его
   * раскрыли. Под тем же корнем `dashboard`, чтобы инвалидация главной задевала и его.
   */
  dashboardActivity: () => [CLIENT_ROOT, 'dashboard', 'activity'] as const,

  /** The document registry (`GET /api/client/documents`) — release Р2. */
  documents: {
    all: [CLIENT_ROOT, 'documents'] as const,
    /** Every page of every filter — the invalidation target after a failed download. */
    lists: () => [...clientKeys.documents.all, 'list'] as const,
    list: (filter?: ClientDocumentFilterRequest) =>
      [...clientKeys.documents.lists(), filter ?? {}] as const,
  },

  /**
   * Additional-work approvals (`/api/client/approvals`, release Р4).
   *
   * `detail(id)` is what the chat card (`ClientApprovalCard`) reads — the payload carries
   * no status or money (decision B3), so the card's live state, including whether the
   * customer can still respond, comes from here, `staleTime: 30_000`.
   */
  approvals: {
    all: [CLIENT_ROOT, 'approvals'] as const,
    lists: () => [...clientKeys.approvals.all, 'list'] as const,
    list: (filter?: ClientApprovalFilterRequest) =>
      [...clientKeys.approvals.lists(), filter ?? {}] as const,
    details: () => [...clientKeys.approvals.all, 'detail'] as const,
    detail: (id: string) => [...clientKeys.approvals.details(), id] as const,
  },

  /**
   * The company's other portal accounts (`GET /api/client/users/colleagues`) — Р2.
   *
   * Separate from {@link clientKeys.company}: the company card is a directory record and
   * its `contacts` are contact PEOPLE, while this is the list of accounts that can sign
   * in. Two different sets, two different endpoints, two different keys.
   */
  colleagues: () => [CLIENT_ROOT, 'colleagues'] as const,

  /** The customer company card. */
  company: () => [CLIENT_ROOT, 'company'] as const,

  /** `GET /api/client/users/me`. */
  profile: () => [CLIENT_ROOT, 'profile'] as const,

  /**
   * The company's portal accounts as the «Сотрудники» section manages them
   * (`GET /api/client/employees`).
   *
   * Separate from {@link clientKeys.colleagues}, which is the read-only roster every colleague
   * sees: this one carries roles and — for a caller who manages people — logins. Two audiences,
   * two shapes, two keys; sharing one would let a manager's copy answer an ordinary employee's
   * screen with contacts on it.
   */
  employees: {
    all: [CLIENT_ROOT, 'employees'] as const,
    lists: () => [CLIENT_ROOT, 'employees', 'list'] as const,
    list: (search?: string) => [...clientKeys.employees.lists(), search ?? ''] as const,
    audit: (page: number) => [CLIENT_ROOT, 'employees', 'audit', page] as const,
  },

  /**
   * The company's own role matrix (`/api/client/roles/*`).
   *
   * `matrix()` is invalidated after every write; the caller's OWN keys ride on
   * {@link clientKeys.profile} instead, because the profile is already re-read at the two moments
   * they can change — page load and company switch.
   */
  roles: {
    all: [CLIENT_ROOT, 'roles'] as const,
    catalog: () => [CLIENT_ROOT, 'roles', 'catalog'] as const,
    matrix: () => [CLIENT_ROOT, 'roles', 'matrix'] as const,
  },

  /**
   * The container registry, «мой парк» (`/api/client/containers`, release Р4).
   *
   * `detail(id)` caches the card's OWN service-history shape (`ClientContainerDto`), a
   * different DTO from the list row it is keyed independently of — same split as
   * {@link applications}.
   */
  containers: {
    all: [CLIENT_ROOT, 'containers'] as const,
    lists: () => [...clientKeys.containers.all, 'list'] as const,
    list: (filter?: ClientContainerFilterRequest) =>
      [...clientKeys.containers.lists(), filter ?? {}] as const,
    details: () => [...clientKeys.containers.all, 'detail'] as const,
    detail: (id: string) => [...clientKeys.containers.details(), id] as const,
    /**
     * The «Тип» filter's vocabulary — the distinct types of this company's own fleet. Under
     * `all` on purpose: adding a container can add a type, and the invalidation that refreshes
     * the registry has to refresh the checklist over it in the same breath.
     */
    types: () => [...clientKeys.containers.all, 'types'] as const,
  },

  /**
   * Service points the company works with, ranked (`GET /api/client/ptos`, release Р4).
   *
   * One key for one request — a flat, unfiltered, unpaged array (contract §3), so there is no
   * per-filter cache entry to key the way {@link containers}.list does.
   */
  ptos: () => [CLIENT_ROOT, 'ptos'] as const,

  /**
   * Every service point a new application may name (`GET /api/client/ptos/selectable`, Р34).
   * Separate from {@link ptos}: same endpoint family, different question and different shape.
   */
  selectableServicePoints: () => [CLIENT_ROOT, 'ptos', 'selectable'] as const,

  /** The company's own energy units (`GET /api/client/equipment`, Р34). */
  equipment: () => [CLIENT_ROOT, 'equipment'] as const,

  /**
   * Signed video playback (`GET /api/client/files/{id}/playback`, video release).
   *
   * One entry per file id — there is no batch endpoint on this surface, unlike the internal
   * `/files/meta` (see `useClientFilesPlayback`, which subscribes to several of these keys at
   * once via `useQueries` rather than widening this shape into a list key).
   */
  files: {
    all: [CLIENT_ROOT, 'files'] as const,
    playback: (id: string) => [...clientKeys.files.all, id, 'playback'] as const,
  },
} as const;
